import { Loader2Icon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type JSX } from 'react';

import { notify } from '@/lib/notify.js';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogClose } from '@/components/ui/dialog.js';

/** Which credential the connect will use. */
type ConnectionMethod = 'sign_in' | 'token';

export interface ConnectionMethodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The connector being connected, in the words the card shows — "Jira", "Azure DevOps". */
  providerName: string;
  /**
   * Whose sign-in the popup runs. Not always the provider itself: Azure DevOps signs in with
   * Microsoft, so naming the provider here would send someone looking for the wrong window.
   */
  signInWith: string;
  /** What this provider calls its token, article included, so each line reads as a sentence. */
  tokenLabel: string;
  /** Disables the choice while the sign-in popup is in flight. */
  busy?: boolean;
  explainUnavailableActions?: boolean;
  /**
   * Called when the person dismisses the dialog — a click outside or Escape — while it is busy. Given,
   * the dismiss cancels that work and closes the dialog; absent, a busy dialog refuses to close.
   */
  onDismissWhileBusy?: () => void;
  /** Run the OAuth connect. */
  onChooseSignIn: () => void | Promise<void>;
  /** Open the credentials form on its token step. */
  onChooseToken: () => void;
}

/**
 * How to connect a provider that accepts both a sign-in and a token — one step in the connect flow,
 * for every provider that has the two.
 *
 * It used to be asked inside the card: the card grew into a two-option chooser with its own Cancel,
 * several times taller than its neighbours, and the question was buried in the grid. A dialog asks it
 * once, in front of everything, and leaves the card the size it was.
 *
 * The choice is about the credential and nothing else. Whichever way is taken, the connection belongs
 * to the person who made it and reaches nobody else — so this screen says nothing about who can use
 * the result, and the copy it replaced (an authorized account "shared with everyone in this
 * workspace", a token as "a shared credential for the whole workspace") was wrong on both counts.
 *
 * It collects no credential of its own: the token lane hands over to the provider's own form, which
 * is the only place that knows what that provider needs.
 */
export function ConnectionMethodDialog({
  open,
  onOpenChange,
  providerName,
  signInWith,
  tokenLabel,
  busy = false,
  explainUnavailableActions = false,
  onDismissWhileBusy,
  onChooseSignIn,
  onChooseToken,
}: ConnectionMethodDialogProps): JSX.Element {
  const confirming = useRef(false);
  const warnBusy = (): void => {
    notify.warning(
      'A connection request is running. Finish sign-in, or cancel it before trying again.',
    );
  };
  const changeOpen = (next: boolean): void => {
    if (!next && (busy || confirming.current)) {
      if (!onDismissWhileBusy) {
        if (explainUnavailableActions)
          notify.warning(
            'Connection details have already been sent. Wait for saving to finish before closing.',
          );
        return;
      }
      onDismissWhileBusy();
    }
    onOpenChange(next);
  };
  const [method, setMethod] = useState<ConnectionMethod>('sign_in');

  /** Reset on open, so a dialog reopened after a dismissed popup starts on the easier path again. */
  useEffect(() => {
    if (open) setMethod('sign_in');
  }, [open]);

  const confirm = (): void => {
    if (busy || confirming.current) {
      if (explainUnavailableActions) warnBusy();
      return;
    }
    confirming.current = true;
    void (async () => {
      try {
        if (method === 'sign_in') await onChooseSignIn();
        else onChooseToken();
      } finally {
        confirming.current = false;
      }
    })();
  };

  const heading = `How do you want to connect ${providerName}?`;

  return (
    <AppDialog
      open={open}
      /** Block Escape / overlay-driven close while the sign-in popup is in flight. */
      onOpenChange={changeOpen}
      title={heading}
      description="Pick the credential to connect with."
      titleHidden
      showCloseButton={false}
      className="flex w-full max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
    >
      <div className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {heading}
          </h3>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={!explainUnavailableActions && busy}
              className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </div>

        <fieldset className="mt-4 flex flex-col gap-4 border-0 p-0">
          <legend className="text-compact leading-relaxed text-muted-foreground">
            Two ways in — pick whichever you have.
          </legend>

          <label
            htmlFor="connection-method-sign-in"
            className="flex cursor-pointer items-start gap-2"
          >
            <input
              id="connection-method-sign-in"
              type="radio"
              name="connection-method"
              value="sign_in"
              checked={method === 'sign_in'}
              onChange={() => {
                if (busy || confirming.current) {
                  if (explainUnavailableActions) warnBusy();
                  return;
                }
                setMethod('sign_in');
              }}
              disabled={!explainUnavailableActions && busy}
              className="mt-0.5 size-4 shrink-0 accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-medium text-foreground">
                Sign in with {signInWith}
              </span>
              <span className="text-caption leading-relaxed text-muted-foreground">
                A {signInWith} window opens and you authorize the assistant.
                Nothing to copy or paste.
              </span>
            </span>
          </label>

          <label
            htmlFor="connection-method-token"
            className="flex cursor-pointer items-start gap-2"
          >
            <input
              id="connection-method-token"
              type="radio"
              name="connection-method"
              value="token"
              checked={method === 'token'}
              onChange={() => {
                if (busy || confirming.current) {
                  if (explainUnavailableActions) warnBusy();
                  return;
                }
                setMethod('token');
              }}
              disabled={!explainUnavailableActions && busy}
              className="mt-0.5 size-4 shrink-0 accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-medium text-foreground">
                Use {tokenLabel}
              </span>
              <span className="text-caption leading-relaxed text-muted-foreground">
                You create {tokenLabel} yourself and paste it on the next
                screen.
              </span>
            </span>
          </label>
        </fieldset>
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => changeOpen(false)}
          disabled={!explainUnavailableActions && busy}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={confirm}
          disabled={!explainUnavailableActions && busy}
        >
          {busy && (
            <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
          )}
          Continue
        </Button>
      </div>
    </AppDialog>
  );
}
